import { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { Input, Select, Textarea } from '@fluentui/react-components';
import { MessageBarType } from '@fluentui/react';
import { useTranslation } from 'react-i18next';

import { NewTenantPriceList } from '../../../../models/Entities/TenantPriceLists/NewTenantPriceList';
import { TenantPriceList } from '../../../../models/Entities/TenantPriceLists/TenantPriceList';
import { MessageComponent } from '../../../../commons/component/MessageComponent';
import * as ComponentFunctions from './TenantPriceListFormFunctions';
import { GuideDrawer } from '../../../../theme/components/GuideDrawer';
import { GuideField } from '../../../../theme/components/GuideField';

export const TenantPriceListFormComponent = (props: ComponentFunctions.Props) => {
  const [t] = useTranslation();

  const [submittedForm, setSubmittedForm] = useState<boolean>(false);
  const [formValidation, setFormValidation] = useState<
    ComponentFunctions.FormValidation | undefined
  >();
  const [formState, setFormState] = useState<ComponentFunctions.TenantPriceListFormState>(
    ComponentFunctions.initialFormState
  );

  // ── Effects ────────────────────────────────────────────────────────────────

  useEffect(() => {
    if (props.TenantPriceList) {
      setFormState(ComponentFunctions.formStateFromTenantPriceList(props.TenantPriceList));
    } else {
      ResetForm();
    }
  }, [props.TenantPriceList]);

  useEffect(() => {
    if (submittedForm && (props.addingSuccess || props.savingSuccess)) {
      ResetForm();
      props.dismissPanel();
    }
  }, [props.addingSuccess, props.savingSuccess, submittedForm]);

  // ── Helpers ────────────────────────────────────────────────────────────────

  const ResetForm = () => {
    setFormState(ComponentFunctions.initialFormState);
    setFormValidation(undefined);
    setSubmittedForm(false);
  };

  const patch = (partial: Partial<ComponentFunctions.TenantPriceListFormState>) =>
    setFormState((s) => ({ ...s, ...partial }));

  // ── Events ─────────────────────────────────────────────────────────────────

  const HandleCancelForm = () => {
    ResetForm();
    props.dismissPanel();
  };

  const HandleSaveForm = () => {
    setSubmittedForm(true);
    const validation = ComponentFunctions.validateForm(formState, t);
    setFormValidation(validation);

    if (validation.success) {
      if (props.TenantPriceList) {
        const updated = {
          ...props.TenantPriceList,
          listName: formState.listName!,
          comments: formState.comments || '',
          priceListType: formState.priceListType!
        };
        props.UpdateTenantPriceList(updated as TenantPriceList);
      } else {
        const newItem: NewTenantPriceList = {
          listName: formState.listName!,
          comments: formState.comments || '',
          priceListType: formState.priceListType!
        };
        props.AddTenantPriceList(newItem);
      }
    }
  };

  // ── Render ─────────────────────────────────────────────────────────────────

  const isSaving = (props.isAdding || props.isSaving) ?? false;

  const requiredMessage = t('tenantPriceListForm.formFields.requiredField');

  return (
    <GuideDrawer
      open={props.isOpen}
      size="small"
      title={t('tenantPriceListForm.modal.priceList')}
      onDismiss={HandleCancelForm}
      closeLabel={t('tenantPriceListForm.modal.closeButton')}
      testId="tenant-price-list-drawer"
      secondaryAction={{ label: t('tenantPriceListForm.footer.cancelButton'), disabled: isSaving }}
      primaryAction={{
        label: t('tenantPriceListForm.footer.saveButton'),
        onClick: HandleSaveForm,
        loading: isSaving,
      }}
    >
      {submittedForm && formValidation?.success === false && (
        <MessageComponent
          message={t('tenantPriceListForm.modal.reviewInformation')}
          type={MessageBarType.error}
          itemList={formValidation.errors}
        />
      )}

      {submittedForm &&
        (props.failOnSaving || props.failOnAdding) &&
        props.error && (
          <MessageComponent
            message={t('tenantPriceListForm.modal.errorMessage')}
            subMessage={props.error.ErrorMessage}
            itemList={props.error.Errors}
            type={MessageBarType.error}
          />
        )}

      <GuideField
        label={t('tenantPriceListForm.formFields.name')}
        required
        error={submittedForm && !formState.listName?.trim() ? requiredMessage : undefined}
      >
        <Input
          value={formState.listName ?? ''}
          onChange={(_, data) => patch({ listName: data.value || undefined })}
          autoComplete="off"
        />
      </GuideField>

      <GuideField
        label={t('tenantPriceListForm.formFields.type')}
        required
        error={submittedForm && !formState.priceListType ? requiredMessage : undefined}
      >
        <Select
          title={t('tenantPriceListForm.formFields.type')}
          value={formState.priceListType ?? ''}
          onChange={(_, data) => patch({ priceListType: data.value || undefined })}
        >
          <option value="">{t('tenantPriceListForm.formFields.select')}</option>
          {ComponentFunctions.PriceListTypeOptions.map((opt) => (
            <option key={opt.key as string} value={opt.key as string}>
              {opt.text}
            </option>
          ))}
        </Select>
      </GuideField>

      <GuideField label={t('tenantPriceListForm.formFields.comments')}>
        <Textarea
          value={formState.comments ?? ''}
          resize="vertical"
          onChange={(_, data) => patch({ comments: data.value || undefined })}
        />
      </GuideField>
    </GuideDrawer>
  );
};

export default connect(
  ComponentFunctions.mapStateToProps,
  ComponentFunctions.mapDispatchToProps
)(TenantPriceListFormComponent as any);

